import { useState, useEffect, useRef } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';
import Router from 'next/router';

import Dropdown from '../shared/searchbar-dropdown.component';

import HorizontalDateRangePicker from '../shared/horizontal-date-range-picker.component';

import { useCitiesData, useSelectedCity } from '../../domain/city/city.domain'

import { eventService } from '../../services/homii-services';

import { CONSTANTS } from '../../../utils/constants';

const defaultCityText = 'Choose Location';
const defaultStyleText = 'Private or Shared';

const styleOptionsEnabled = [
    {
        value: 'private_shared_apartment',
        label: 'Private or Shared',
        subLabel: 'Private apartment in shared living space',
        id: 'private_shared_apartment_select',
    },
    {
        value: 'private_apartment',
        label: 'Private Apartment',
        subLabel: 'Entire apartment for yourself',
        id: 'private_apartment_select',
    },
    {
        value: 'private_bedroom',
        label: 'Private Bedroom',
        subLabel: 'Room in shared living space',
        id: 'private_bedroom_select',
        isDisabled: false,
    },
    {
        value: 'shared_male',
        label: 'Shared - Male Only',
        subLabel: 'Stay with a male HOMii',
        id: 'shared_select_male',
        isDisabled: false,
    },
    {
        value: 'shared_female',
        label: 'Shared - Female Only',
        subLabel: 'Stay with a female HOMii',
        id: 'shared_select_female',
        isDisabled: false,
    },
];

const SearchBar = (props) => {

    const { query = {}, type } = props;

    const { data: cities } = useCitiesData();

    const { data: selectedCity } = useSelectedCity();

    const selectedCityContainerRef = useRef(null);

    const dateText = 'Select Dates';    

    const [state, setState] = useState(() => {
        const cityParam = _.get(query, 'city');
        const styleParam = _.get(query, 'style') || 'private_shared_apartment';
        const startDateParam = _.get(query, 'startDate');
        const endDateParam = _.get(query, 'endDate');

        let initialCity = cityParam;
        if (!cityParam && selectedCity) {
            initialCity = selectedCity === 'Nearby' ? selectedCity : selectedCity.name;
        }

        const showDatePicker = !!(startDateParam || endDateParam);

        return {
            city: initialCity || 'Choose Location',
            style: styleParam,
            monthlyStartDate: startDateParam || moment().format('YYYY-MM-DD'),
            monthlyEndDate: endDateParam || moment().add(1, 'months').startOf('month').format('YYYY-MM-DD'),
            showDatePicker: showDatePicker,
            tempSelection: null,
            dateChanged: false,
            styleOptions: styleOptionsEnabled,
            shouldUpdate: true,
        };
    });

    useEffect(() => {
        const cityParam = _.get(query, 'city');
        if (!cityParam && selectedCity && state.city !== selectedCity.name) {
            setState((prevState) => ({
                ...prevState,
                city: selectedCity.name,
            }));
        }
    }, [selectedCity, query, state.city]);    

    useEffect(() => {        
        const startDateParam = _.get(query, 'startDate');
        const endDateParam = _.get(query, 'endDate');
        if (startDateParam && state.monthlyStartDate !== startDateParam) {
           setState((prevState) => ({
               ...prevState,
               monthlyStartDate: startDateParam,
               showDatePicker: true
           }));
        }
        if (endDateParam && state.monthlyEndDate !== endDateParam) {
            setState((prevState) => ({
                ...prevState,
                monthlyEndDate: endDateParam,
                showDatePicker: true
            }));
        }
    }, [query, state.monthlyStartDate, state.monthlyEndDate, state.showDatePicker]);

    const onCityChange = (value) => {
        const { city } = state;
        const newQuery = {
            city: value ? value : city,
        };
        Router.push(
            {
                pathname: '/bookings',
                query: newQuery,
            },
            undefined,
            { shallow: false },
        );
    };

    const onStyleChange = (value) => {
        setState((prev) => ({
            ...prev,
            style: value
        }));
    };

    const onDateRangeChange = (startDate, endDate) => {
        setState((prev) => ({
            ...prev,
            monthlyStartDate: moment(startDate).format('YYYY-MM-DD'),
            monthlyEndDate: moment(endDate).format('YYYY-MM-DD'),
            dateChanged: true,
        }));
    };

    const isOutsideRangeDaily = (date) => {
        if (!state.monthlyStartDate) {
            return false;
        }
        return moment(date).format('YYYY-MM-DD') < moment().format('YYYY-MM-DD');
    };

    const onSelectDatesClick = () => {
        setState((prev) => ({ ...prev, showDatePicker: true }));
    };

    const buildCityOptions = () => {
        const sortedCities = _.sortBy(cities, ['name']);
        return _.map(sortedCities, (_city) => ({
            ..._city,
            label: _city.name,
            value: _city.name,
        }));
    };

    const getDefaultOptions = (queryParam, Options) => {
        if (!Options) return null;
        return Options.find((option) => option.value === queryParam) || null;
    };

    const renderCityItems = () => {
        const cityOptions = buildCityOptions();
        cityOptions.unshift({ id: CONSTANTS.NEARBYID, value: CONSTANTS.NEARBY, label: CONSTANTS.NEARBY });
        const defaultOptions = getDefaultOptions(state.city, cityOptions);
        return (
            <div className='search-content-wrapper' ref={selectedCityContainerRef}>
                <span className='search-content-wrapper-text'>Location</span>
                <Dropdown
                    onChange={(value) => onCityChange(value)}
                    options={cityOptions}
                    placeholder={defaultCityText}
                    defaultSelected={defaultOptions}
                    selectId="city_dropdown"
                />
            </div>
        );
    };

    const renderStyleItems = () => {
        const styleParam = _.get(query, 'style') === undefined ? 'private_shared_apartment' : _.get(query, 'style');
        const defaultOptions = getDefaultOptions(styleParam, styleOptionsEnabled);
        return (
            <div className='search-content-wrapper'>
                <span className='search-content-wrapper-text'>Style</span>
                <Dropdown
                    onChange={(value) => onStyleChange(value)}
                    options={styleOptionsEnabled}
                    placeholder={defaultStyleText}
                    defaultSelected={defaultOptions}
                    selectId="style_dropdown"
                />
            </div>
        );
    };

    const renderDateItems = () => {
        const { showDatePicker, monthlyStartDate, monthlyEndDate } = state;
        if (!showDatePicker) {
            return (
                <button id="select_dates_button" onClick={onSelectDatesClick} className='search-container-date-button'>
                    {dateText}
                </button>
            );
        }
        if (showDatePicker && monthlyStartDate && monthlyEndDate) {
            return (
                <HorizontalDateRangePicker
                    startDate={moment(monthlyStartDate)}
                    endDate={moment(monthlyEndDate)}
                    onDateRangeChange={onDateRangeChange}
                    isOutsideRange={isOutsideRangeDaily}
                    dateFormat="DD MMM"
                    offset
                />
            );
        }
        return null;
    };

    const onSearchClick = () => {

        const { city, style } = state;

        const newQuery = { ...query };

        const pageParam = _.get(query, 'page');

        const container = selectedCityContainerRef?.current;
        const singleValueDiv = container?.querySelector('div[class*="singleValue"]');
        const cityValue = singleValueDiv?.textContent; 
        
        if(cityValue && cityValue !== city) {
            newQuery.city = cityValue;
        }else {
            newQuery.city = city;
        }        

        if (style !== defaultStyleText) {
            newQuery.style = style;
        }

        if (pageParam) {
            delete newQuery.page;
        }

        if (type === 'Portfolio') {
            newQuery.portfolioId = portfolioId;
        }

        Router.push(
            {
                pathname: '/bookings',
                query: newQuery,
            },
            undefined,
            { shallow: false },
        );

    }

    const renderLanding = () => {
        return (
            <div className='search-container-landing'>
                <div className='search-container-row'>
                    <div className='search-container-lading-col-2'>
                        <div className='search-container-divider'>{renderCityItems()}</div>
                    </div>
                    <div className='search-container-lading-col-1'>
                        <Button
                            id="search_button"
                            className='search-container-button'
                            startIcon={<SearchIcon />}
                            onClick={onSearchClick}
                        >
                            Search
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const renderMobile = () => {
        return (
            <div className='mobile-search-bar-container'>
                <div className='mobile-search-bar-row'>
                    <div className='mobile-search-bar-col-city'>{renderCityItems()}</div>
                    <div className='mobile-search-bar-col-button'>
                        <Button
                            id="search_button"
                            className='search-container-button'
                            startIcon={<SearchIcon />}
                            onClick={onSearchClick}
                        >
                            Search
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    const onFindaSpaceClick = () => {
        const {
            city,
            style,
            monthlyStartDate,
            monthlyEndDate,
        } = state;

        const {
            query,
            selectedCity,
        } = props;

        const buildingParam = _.get(query, 'building');

        const pageParam = _.get(query, 'page');

        let shallow = false;
        let startDate = moment(monthlyStartDate).format('YYYY-MM-DD');
        let endDate = moment(monthlyEndDate).format('YYYY-MM-DD');

        setState({
            tempSelection: { selectedStyle: style },
            dateChanged: false,
        });

        // Use city from query if it exists, otherwise use city from state
        const cityParam = query.city || city;

        const filterObject = {
            city: cityParam,
            style,
            startDate,
            endDate,
        };

        filterObject.description = JSON.stringify(filterObject);

        eventService.logCustomEvent('desktop_search_filters', filterObject);

        const newQuery = { ...query };

        newQuery.city = cityParam;

        if (style !== defaultStyleText) {
            newQuery.style = style;
        }

        if (buildingParam || !_.isEmpty(buildingParam)) {
            newQuery.building = buildingParam;
        } else {
            delete newQuery.building;
        }

        if (selectedCity?.name !== city) {
            delete newQuery.building;
        }

        if (startDate) {
            newQuery.startDate = moment(startDate).format('YYYY-MM-DD');
        }

        if (endDate) {
            newQuery.endDate = moment(endDate).format('YYYY-MM-DD');
        }

        if (pageParam) {
            delete newQuery.page;
        }

        Router.push(
            {
                pathname: '/bookings',
                query: newQuery,
            },
            undefined,
            { shallow },
        );
    }

    const renderBookings = () => {

        const { city, style, tempSelection, dateChanged } = state;

        const cityParam = _.get(query, 'city');

        let buttonText = 'Find a Space';
        let startIcon = null;

        if (city !== cityParam ||
            (style !== defaultStyleText && _.get(tempSelection, 'selectedStyle') !== style) ||
            dateChanged) {
            buttonText = 'Search';
            startIcon = <SearchIcon />;
        }

        return (

            <div className='search-container'>

                <div className='search-container-row'>

                    <div className='search-container-column'>
                        <div className='search-container-divider'>
                            {renderCityItems()}
                        </div>
                    </div>

                    <div className='search-container-column'>
                        <div className='search-container-divider'>
                            {renderStyleItems()}
                        </div>
                    </div>

                    <div className='search-container-column-last'>
                        <span className='search-container-column-text'>Check-in / Check-out</span>
                        {renderDateItems()}
                    </div>

                    <div className='search-container-column-button'>
                        <Button
                            className='search-container-button'
                            id="find_space_button_bar"
                            startIcon={startIcon}
                            onClick={onFindaSpaceClick}
                        >
                            {buttonText}
                        </Button>
                    </div>

                </div>

            </div>
        );
    };

    const renderSearchBar = () => {
        return (
            <>
                {type === 'Landing' || type === 'Portfolio' ? renderLanding() : null}
                {type === 'Bookings' ? renderBookings() : null}
                {type === 'Mobile' ? renderMobile() : null}
            </>
        );
    };

    const renderLoading = () => {
        return (
            <div className={type === 'Bookings' ? 'search-container' : 'search-container-landing'}>
                <div className='search-container-loading search-container-row'>
                    <CircularProgress />
                </div>
            </div>
        );
    };

    return (
        <>
            {!selectedCity ? renderLoading() : renderSearchBar()}
        </>
    );

};

SearchBar.propTypes = {
    query: PropTypes.object,
    type: PropTypes.string
};

export default SearchBar;